/**
 * DaddyTalkInbox — recruiter's inbox for candidate chats initiated on
 * DaddyTalk. Two-pane layout: thread list on the left, active thread
 * conversation on the right.
 *
 * Wired to M2 endpoints:
 *   GET  /daddytalk/threads?as_role=target       — inbox
 *   GET  /daddytalk/threads/{id}                 — conversation
 *   POST /daddytalk/threads/{id}/messages        — send reply
 *   PUT  /daddytalk/threads/{id}/status          — accept/decline/close
 *
 * Auto-refresh every 20s via polling (WebSocket upgrade is on the
 * backlog). Poll pauses when the tab is hidden to conserve mobile
 * battery.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    ChatCircleDots,
    PaperPlaneTilt,
    CheckCircle,
    XCircle,
    Circle,
    Clock,
    UserCircle,
    ArrowLeft,
    Rocket,
    UsersThree,
    CaretDown,
    MagnifyingGlass,
    Microphone,
    ChatText,
    X,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import FastTrackModal from "@/components/FastTrackModal";
import PushOptInBanner from "@/components/PushOptInBanner";
import { VoiceRecorder, VoicePlayer } from "@/components/chat/VoiceRecorder";
import {
    useSwipeToReply,
    ReplyQuote,
    ReplyPreviewBar,
    SwipeReplyIndicator,
} from "@/components/chat/SwipeToReply";
import ForwardMessageModal from "@/components/chat/ForwardMessageModal";
import ChatAvatar from "@/components/chat/ChatAvatar";
import { useChatDensity, ChatDensityToggle } from "@/lib/useChatDensity";
import {
    ReactionBar,
    ReactionPicker,
    QuickReactionsHoverStrip,
    useLongPress,
} from "@/components/chat/ReactionPicker";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const REFRESH_INTERVAL_MS = 15000;  // fallback polling — 15s so latency stays low even if SSE drops

function formatWhen(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return d.toLocaleDateString();
    } catch { return ""; }
}

export default function DaddyTalkInbox() {
    const { user } = useAuth();
    const [sp] = useSearchParams();
    const [threads, setThreads] = useState([]);
    const [selectedId, setSelectedId] = useState(sp.get("thread") || null);
    const [convo, setConvo] = useState(null);
    const [density, setDensity] = useChatDensity();
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [reply, setReply] = useState("");
    const [filter, setFilter] = useState("all"); // all | pending | accepted | closed
    const [ftOpen, setFtOpen] = useState(false);
    // V1.8.8 — free-text search across text bodies + voice transcripts
    const [searchQ, setSearchQ] = useState("");
    const [searchHits, setSearchHits] = useState(null);   // null = not searching; [] = zero hits
    const [searchLoading, setSearchLoading] = useState(false);
    const [highlightMsgId, setHighlightMsgId] = useState(null);
    const [replyTo, setReplyTo] = useState(null);  // V1.9.27
    const [forwardMsg, setForwardMsg] = useState(null);  // V1.9.29
    const pollRef = useRef();
    const searchDebounceRef = useRef();

    const loadThreads = useCallback(async () => {
        try {
            const res = await api.get("/daddytalk/threads?as_role=target");
            setThreads(res?.data?.items || []);
        } catch (err) {
            // 401 means DaddyTalk endpoints aren't enabled for this user — silent
            if (err?.response?.status !== 401) {
                console.error("Inbox load failed", err);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const loadConvo = useCallback(async (id) => {
        if (!id) { setConvo(null); return; }
        try {
            const res = await api.get(`/daddytalk/threads/${id}`);
            setConvo(res?.data || null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load conversation");
        }
    }, []);

    // V1.8.8 — debounced full-text search across text + voice transcripts
    useEffect(() => {
        clearTimeout(searchDebounceRef.current);
        const q = searchQ.trim();
        if (q.length < 2) {
            setSearchHits(null);
            setSearchLoading(false);
            return;
        }
        setSearchLoading(true);
        searchDebounceRef.current = setTimeout(async () => {
            try {
                const res = await api.get(`/daddytalk/threads/search?q=${encodeURIComponent(q)}`);
                setSearchHits(res?.data?.items || []);
            } catch (err) {
                if (err?.response?.status !== 401) {
                    console.error("Search failed", err);
                    toast.error("Search failed");
                }
                setSearchHits([]);
            } finally { setSearchLoading(false); }
        }, 300);
        return () => clearTimeout(searchDebounceRef.current);
    }, [searchQ]);

    const openHit = useCallback(async (hit) => {
        // Open the thread and scroll+flash the matched message.
        setSelectedId(hit.thread_id);
        setHighlightMsgId(hit.message_id);
        // Clear the query so the sidebar switches back to thread mode.
        setSearchQ("");
        setSearchHits(null);
    }, []);

    // Initial load + polling
    useEffect(() => {
        loadThreads();
        const tick = () => {
            if (document.visibilityState === "visible") {
                loadThreads();
                if (selectedId) loadConvo(selectedId);
            }
        };
        pollRef.current = setInterval(tick, REFRESH_INTERVAL_MS);
        return () => clearInterval(pollRef.current);
    }, [loadThreads, loadConvo, selectedId]);

    // V1.6.0 M4 — Real-time SSE integration. features.jsx keeps a single
    // EventSource open and rebroadcasts DaddyTalk events on `window` so
    // any component can subscribe cheaply. We refresh the inbox on new
    // thread events and refresh the active conversation on messages
    // that belong to it.
    useEffect(() => {
        const onThreadCreated = () => {
            loadThreads();
        };
        const onMessageNew = (ev) => {
            const detail = ev?.detail || {};
            // If the new message belongs to the thread we're viewing,
            // refresh the conversation pane so the incoming bubble
            // appears without waiting for the 60s poll.
            if (detail.thread_id && detail.thread_id === selectedId) {
                loadConvo(selectedId);
            }
            // Always refresh the thread list — its preview + unread
            // dot need to update.
            loadThreads();
        };
        window.addEventListener("daddytalk:thread-created", onThreadCreated);
        window.addEventListener("daddytalk:message-new", onMessageNew);
        const onReacted = (ev) => {
            const detail = ev?.detail || {};
            if (detail.thread_id !== selectedId) return;
            setConvo((prev) => prev ? ({
                ...prev,
                messages: prev.messages.map((m) =>
                    m.id === detail.message_id ? { ...m, reactions: detail.reactions } : m,
                ),
            }) : prev);
        };
        window.addEventListener("daddytalk:message-reacted", onReacted);
        const onUpdated = (ev) => {
            const detail = ev?.detail || {};
            if (detail.thread_id !== selectedId) return;
            setConvo((prev) => prev ? ({
                ...prev,
                messages: prev.messages.map((m) => {
                    if (m.id !== detail.message_id) return m;
                    const attPatch = detail.attachment || {};
                    return { ...m, attachment: { ...(m.attachment || {}), ...attPatch } };
                }),
            }) : prev);
        };
        window.addEventListener("daddytalk:message-updated", onUpdated);
        return () => {
            window.removeEventListener("daddytalk:thread-created", onThreadCreated);
            window.removeEventListener("daddytalk:message-new", onMessageNew);
            window.removeEventListener("daddytalk:message-reacted", onReacted);
            window.removeEventListener("daddytalk:message-updated", onUpdated);
        };
    }, [loadThreads, loadConvo, selectedId]);

    // Refresh conversation when selection changes
    useEffect(() => { loadConvo(selectedId); }, [selectedId, loadConvo]);

    // V1.9.34 — starred-thread lookup for filter chip.
    const [starredThreadIds, setStarredThreadIds] = useState(new Set());
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/daddytalk/starred");
                if (cancelled) return;
                const s = new Set((r.data.items || []).map((m) => m.thread_id));
                setStarredThreadIds(s);
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, [convo?.messages?.length]);

    const filtered = useMemo(() => {
        if (filter === "all") return threads;
        if (filter === "unread") return threads.filter((t) => t.unread);
        if (filter === "starred") return threads.filter((t) => starredThreadIds.has(t.id));
        return threads.filter((t) => t.status === filter);
    }, [threads, filter, starredThreadIds]);

    const unreadCount = threads.filter((t) => t.unread && t.status !== "closed").length;

    async function setStatus(id, status) {
        try {
            await api.put(`/daddytalk/threads/${id}/status`, { status });
            toast.success(
                status === "accepted" ? "Chat accepted — candidate has been notified." :
                status === "declined" ? "Chat declined." :
                status === "closed" ? "Chat closed." : "Status updated.",
            );
            await loadThreads();
            if (id === selectedId) loadConvo(id);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't update status");
        }
    }

    async function sendReply() {
        const body = reply.trim();
        if (!body || !selectedId || sending) return;
        setSending(true);
        try {
            await api.post(`/daddytalk/threads/${selectedId}/messages`, {
                body,
                ...(replyTo ? { reply_to_message_id: replyTo.id } : {}),
            });
            setReply("");
            setReplyTo(null);
            await loadConvo(selectedId);
            await loadThreads();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send reply");
        } finally {
            setSending(false);
        }
    }

    async function sendVoice(blob, durationMs) {
        if (!selectedId || sending) return;
        setSending(true);
        try {
            const fd = new FormData();
            fd.append("file", blob, `voice.${(blob.type.split("/")[1] || "webm").split(";")[0]}`);
            fd.append("duration_ms", String(durationMs));
            if (replyTo) fd.append("reply_to_message_id", replyTo.id);
            await api.post(`/daddytalk/threads/${selectedId}/messages/voice`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setReplyTo(null);
            await loadConvo(selectedId);
            await loadThreads();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send voice note");
        } finally { setSending(false); }
    }

    async function handleReact(messageId, emoji) {
        setConvo((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                messages: prev.messages.map((m) => {
                    if (m.id !== messageId) return m;
                    const rx = { ...(m.reactions || {}) };
                    const list = new Set(rx[emoji] || []);
                    if (list.has(user?.id)) list.delete(user?.id); else list.add(user?.id);
                    if (list.size === 0) delete rx[emoji]; else rx[emoji] = [...list];
                    return { ...m, reactions: rx };
                }),
            };
        });
        try {
            await api.post(`/daddytalk/messages/${messageId}/react`, { emoji });
        } catch (err) {
            toast.error("Couldn't save reaction. Please retry.");
            loadConvo(selectedId);
        }
    }

    async function handleStar(messageId) {
        setConvo((prev) => {
            if (!prev) return prev;
            return { ...prev, messages: prev.messages.map((m) => {
                if (m.id !== messageId) return m;
                const arr = new Set(m.starred_by_user_ids || []);
                if (arr.has(user?.id)) arr.delete(user?.id); else arr.add(user?.id);
                return { ...m, starred_by_user_ids: [...arr] };
            }) };
        });
        try { await api.post(`/daddytalk/messages/${messageId}/star`); }
        catch (err) {
            toast.error("Couldn't toggle star. Please retry.");
            loadConvo(selectedId);
        }
    }

    return (
        <div
            className="flex-1 min-h-0 flex flex-col"
            data-testid="daddytalk-inbox-root"
        >
            <PushOptInBanner user={user} context="daddytalk" />
            <div className="px-4 md:px-6 py-4 border-b border-ink/10 bg-white flex items-center gap-3 flex-wrap md:flex-nowrap md:justify-between">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <img
                        src="/mascot/daddytalk/avatar_hello.png"
                        alt=""
                        className="w-10 h-10 rounded-full border-2 border-cream shrink-0"
                    />
                    <div className="min-w-0">
                        <h1 className="font-heading text-base md:text-lg font-black leading-tight truncate">DaddyTalk Inbox</h1>
                        <p className="text-xs text-ink/50 truncate">
                            {unreadCount > 0
                                ? `${unreadCount} unread chat${unreadCount === 1 ? "" : "s"} waiting`
                                : "You're all caught up"}
                        </p>
                    </div>
                </div>
                <div
                    className="flex items-center gap-1 overflow-x-auto w-full md:w-auto md:overflow-visible no-scrollbar"
                    data-testid="daddytalk-inbox-filters"
                >
                    {[
                        { id: "all", label: "All" },
                        { id: "unread", label: "Unread", count: unreadCount },
                        { id: "pending", label: "Pending" },
                        { id: "accepted", label: "Active" },
                        { id: "closed", label: "Closed" },
                        { id: "starred", label: "Starred", count: starredThreadIds.size },
                    ].map(({ id, label, count }) => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setFilter(id)}
                            data-testid={`daddytalk-inbox-filter-${id}`}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider transition-colors shrink-0 rounded-full ${
                                filter === id
                                    ? "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300"
                                    : "text-ink/50 hover:text-ink hover:bg-black/5"
                            }`}
                        >
                            {label}
                            {typeof count === "number" && count > 0 && (
                                <span className={`text-[10px] ${filter === id ? "text-emerald-700" : "text-ink/50"}`}>{count}</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* V1.8.8 — Search bar (text + voice-note transcripts) */}
            <div className="px-4 md:px-6 pb-3 border-b border-ink/10 bg-white">
                <div className="relative">
                    <MagnifyingGlass
                        size={14}
                        weight="bold"
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40 pointer-events-none"
                    />
                    <input
                        type="text"
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                        placeholder="Search messages, voice-note transcripts…"
                        data-testid="daddytalk-inbox-search"
                        className="w-full pl-8 pr-8 py-2 text-sm rounded-full border border-ink/15 bg-cream/40 focus:outline-none focus:border-cobalt focus:bg-white"
                    />
                    {searchQ && (
                        <button
                            type="button"
                            onClick={() => setSearchQ("")}
                            aria-label="Clear search"
                            data-testid="daddytalk-inbox-search-clear"
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full text-ink/50 hover:text-ink hover:bg-ink/5 flex items-center justify-center"
                        >
                            <X size={12} weight="bold" />
                        </button>
                    )}
                </div>
            </div>

            <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[minmax(260px,340px)_1fr] gap-0 overflow-hidden">
                {/* Threads list */}
                <aside
                    className={`border-r border-ink/10 bg-cream/50 overflow-y-auto ${
                        selectedId ? "hidden md:block" : "block"
                    }`}
                    data-testid="daddytalk-inbox-thread-list"
                    data-density={density}
                >
                    {searchHits !== null ? (
                        // V1.8.8 — Search results mode.
                        <SearchHitList
                            hits={searchHits}
                            loading={searchLoading}
                            query={searchQ}
                            onPick={openHit}
                        />
                    ) : loading ? (
                        <div className="p-6 text-center text-xs text-ink/50">Loading…</div>
                    ) : filtered.length === 0 ? (
                        <div className="p-6 text-center">
                            <img
                                src="/mascot/daddytalk/avatar_thinking.png"
                                alt=""
                                className="w-20 h-20 rounded-full mx-auto mb-3 shadow-sm"
                            />
                            <p className="font-heading font-bold text-sm mb-1">No chats here yet</p>
                            <p className="text-xs text-ink/50 leading-relaxed">
                                {filter === "all"
                                    ? "Chats from candidates will appear here once you're set to visible in DaddyTalk settings."
                                    : `No ${filter} chats.`}
                            </p>
                        </div>
                    ) : (
                        filtered.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setSelectedId(t.id)}
                                data-testid={`daddytalk-thread-card-${t.id}`}
                                data-role="thread-row"
                                className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${
                                    selectedId === t.id ? "bg-black/5" : "hover:bg-black/5"
                                }`}
                            >
                                <div className="thread-avatar shrink-0">
                                    <ChatAvatar
                                        name={t.counterparty?.name || "Candidate"}
                                        src={t.counterparty?.avatar_url}
                                        size="md"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <p className={`truncate ${t.unread ? "font-black" : "font-bold"} text-[15px] text-ink`}>
                                            {t.counterparty?.name || "Candidate"}
                                        </p>
                                        <span className={`text-[11px] whitespace-nowrap shrink-0 ${t.unread ? "text-emerald-600 font-bold" : "text-ink/45"}`}>
                                            {formatWhen(t.last_message_at)}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 mt-0.5">
                                        <p className={`text-[13px] truncate ${t.unread ? "text-ink/80" : "text-ink/55"}`}>
                                            {t.last_message_from_me ? "You: " : ""}{t.last_message_preview || " "}
                                        </p>
                                        {t.unread && (
                                            <span
                                                aria-label="Unread"
                                                className="min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-white text-[10px] font-black inline-flex items-center justify-center shrink-0"
                                            >•</span>
                                        )}
                                    </div>
                                    {t.status && t.status !== "active" && (
                                        <div className="mt-1 text-[9px] font-black uppercase tracking-wider">
                                            <StatusBadge status={t.status} />
                                        </div>
                                    )}
                                </div>
                            </button>
                        ))
                    )}
                </aside>

                {/* Conversation */}
                <section
                    className={`flex flex-col min-h-0 bg-white overflow-hidden ${
                        selectedId ? "block" : "hidden md:flex"
                    }`}
                    data-testid="daddytalk-inbox-conversation"
                >
                    {!selectedId ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                            <img
                                src="/mascot/daddytalk/avatar_neutral.png"
                                alt=""
                                className="w-24 h-24 rounded-full mb-4 shadow-sm"
                            />
                            <h3 className="font-heading font-bold text-base mb-1">
                                Pick a chat to reply
                            </h3>
                            <p className="text-xs text-ink/50 max-w-sm">
                                Candidates on DaddyTalk skip the application black hole and chat
                                you directly. Aim to reply within 2 hours to keep your response
                                rate strong.
                            </p>
                        </div>
                    ) : !convo ? (
                        <div className="flex-1 flex items-center justify-center text-xs text-ink/50">
                            Loading conversation…
                        </div>
                    ) : (
                        <>
                            {/* Conversation header */}
                            <div className="px-3 py-2.5 border-b border-ink/10 flex items-center gap-3 bg-[#f0f2f5]">
                                <button
                                    type="button"
                                    onClick={() => setSelectedId(null)}
                                    className="md:hidden text-ink/60 hover:text-ink"
                                    aria-label="Back to inbox"
                                >
                                    <ArrowLeft size={18} weight="bold" />
                                </button>
                                <ChatAvatar
                                    name={convo.counterparty?.name || "Candidate"}
                                    src={convo.counterparty?.avatar_url}
                                    size="sm"
                                    testId="daddytalk-convo-avatar"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-[15px] truncate text-ink">
                                        {convo.counterparty?.name || "Candidate"}
                                    </p>
                                    <p className="text-[11px] text-ink/50">
                                        <StatusBadge status={convo.thread.status} />
                                    </p>
                                </div>
                                {convo.thread.status === "pending" && (
                                    <div className="flex gap-1" data-testid="daddytalk-thread-actions">
                                        <button
                                            type="button"
                                            onClick={() => setStatus(convo.thread.id, "accepted")}
                                            data-testid="daddytalk-accept-btn"
                                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-500 text-white text-[10px] font-black uppercase tracking-wider hover:bg-emerald-600"
                                        >
                                            <CheckCircle size={11} weight="bold" />
                                            Accept
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setStatus(convo.thread.id, "declined")}
                                            data-testid="daddytalk-decline-btn"
                                            className="inline-flex items-center gap-1 px-3 py-1.5 border-2 border-red-500 text-red-600 text-[10px] font-black uppercase tracking-wider hover:bg-red-50"
                                        >
                                            <XCircle size={11} weight="bold" />
                                            Decline
                                        </button>
                                    </div>
                                )}
                                {convo.thread.status === "accepted" && (
                                    <>
                                        <AskTeamButton
                                            candidateName={convo.counterparty?.name || "Candidate"}
                                            candidateId={convo.counterparty?.id || convo.thread?.candidate_user_id || null}
                                        />
                                        {!convo.thread.fast_tracked_candidate_id && (
                                            <button
                                                type="button"
                                                onClick={() => setFtOpen(true)}
                                                data-testid="daddytalk-fasttrack-btn"
                                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-cobalt text-white text-[10px] font-black uppercase tracking-wider hover:bg-cobalt-dark"
                                            >
                                                <Rocket size={11} weight="bold" />
                                                Fast-track
                                            </button>
                                        )}
                                        {convo.thread.fast_tracked_candidate_id && (
                                            <span
                                                data-testid="daddytalk-fasttracked-badge"
                                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-wider"
                                            >
                                                <CheckCircle size={11} weight="bold" />
                                                Fast-tracked
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => setStatus(convo.thread.id, "closed")}
                                            data-testid="daddytalk-close-btn"
                                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-ink/20 text-ink/60 text-[10px] font-black uppercase tracking-wider hover:bg-ink/5"
                                        >
                                            Close chat
                                        </button>
                                    </>
                                )}
                                <ChatDensityToggle density={density} setDensity={setDensity} testid="daddytalk-density-toggle" />
                            </div>

                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 chat-surface" data-density={density} data-testid="daddytalk-messages-scroll">
                                {convo.messages.map((m) => {
                                    let replyQuoted = null;
                                    if (m.reply_to_message_id) {
                                        const src = convo.messages.find((x) => x.id === m.reply_to_message_id);
                                        if (src) {
                                            replyQuoted = {
                                                sender_name: src.sender_id === user?.id ? "You" : (convo.counterparty?.name || "Recruiter"),
                                                body: src.body,
                                                attachment_kind: src.attachment?.kind
                                                    || (src.attachment?.content_type?.startsWith("image/") && "image")
                                                    || (src.attachment ? "file" : null),
                                            };
                                        }
                                    }
                                    return (
                                        <MessageBubble
                                            key={m.id}
                                            message={m}
                                            isMine={m.sender_id === user?.id}
                                            myUserId={user?.id}
                                            onReact={handleReact}
                                            onStar={handleStar}
                                            onReply={(msg) => {
                                                setReplyTo({
                                                    id: msg.id,
                                                    sender_name: msg.sender_id === user?.id ? "You" : (convo.counterparty?.name || "Recruiter"),
                                                    body: msg.body,
                                                    attachment_kind: msg.attachment?.kind
                                                        || (msg.attachment?.content_type?.startsWith("image/") && "image")
                                                        || (msg.attachment ? "file" : null),
                                                });
                                            }}
                                            onForward={(msg) => setForwardMsg(msg)}
                                            replyQuoted={replyQuoted}
                                            highlighted={highlightMsgId === m.id}
                                            onHighlightFadeDone={() => setHighlightMsgId(null)}
                                        />
                                    );
                                })}
                            </div>

                            {/* Composer */}
                            {convo.thread.status !== "closed" && convo.thread.status !== "declined" && (
                                <div className="border-t border-ink/10 p-2.5 bg-[#f0f2f5] safe-bottom">
                                    {replyTo && (
                                        <ReplyPreviewBar
                                            quoted={replyTo}
                                            onCancel={() => setReplyTo(null)}
                                        />
                                    )}
                                    <div className="flex gap-2 items-center">
                                        <textarea
                                            value={reply}
                                            rows={1}
                                            onChange={(e) => {
                                                setReply(e.target.value);
                                                const el = e.currentTarget;
                                                el.style.height = "auto";
                                                el.style.height = Math.min(el.scrollHeight, 120) + "px";
                                            }}
                                            onKeyDown={(e) => {
                                                const isTouch = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;
                                                if (e.key === "Enter" && !e.shiftKey && !isTouch) {
                                                    e.preventDefault();
                                                    sendReply();
                                                }
                                            }}
                                            placeholder="Message"
                                            data-testid="daddytalk-reply-input"
                                            disabled={convo.thread.status === "pending"}
                                            className="flex-1 min-h-[40px] max-h-[120px] px-4 py-2 bg-white text-[14px] leading-[20px] focus:outline-none disabled:bg-ink/5 disabled:cursor-not-allowed rounded-2xl shadow-sm placeholder:text-ink/40 resize-none overflow-y-auto"
                                        />
                                        {reply.trim() ? (
                                            <button
                                                type="button"
                                                onClick={sendReply}
                                                disabled={!reply.trim() || sending || convo.thread.status === "pending"}
                                                data-testid="daddytalk-reply-send"
                                                className="btn-gradient inline-flex items-center justify-center gap-1.5 h-10 px-5 shrink-0 text-[11px] font-black uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed rounded-full"
                                            >
                                                {sending ? "Sending…" : "Send"}
                                                <PaperPlaneTilt size={11} weight="bold" />
                                            </button>
                                        ) : (
                                            <VoiceRecorder
                                                onSend={sendVoice}
                                                disabled={sending || convo.thread.status === "pending"}
                                                onError={(m) => toast.error(m)}
                                                testId="daddytalk-voice-record-btn"
                                            />
                                        )}
                                    </div>
                                    {convo.thread.status === "pending" && (
                                        <p className="text-[10px] text-ink/40 mt-2 flex items-center gap-1.5">
                                            <Clock size={10} weight="bold" />
                                            Accept the chat first to start replying.
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </section>
            </div>

            {/* Fast-track modal */}
            <FastTrackModal
                open={ftOpen}
                threadId={convo?.thread?.id}
                candidateName={convo?.counterparty?.name || "the candidate"}
                onClose={() => setFtOpen(false)}
                onDone={() => {
                    // Refresh conversation + thread list so the fast-tracked
                    // badge appears and Close-chat becomes the only action.
                    loadConvo(selectedId);
                    loadThreads();
                }}
            />
            <ForwardMessageModal
                open={!!forwardMsg}
                onClose={() => setForwardMsg(null)}
                sourceMessage={forwardMsg}
                context="daddytalk"
            />
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        pending: { label: "Pending", cls: "bg-amber-100 text-amber-700" },
        accepted: { label: "Active", cls: "bg-emerald-100 text-emerald-700" },
        declined: { label: "Declined", cls: "bg-red-100 text-red-700" },
        closed: { label: "Closed", cls: "bg-ink/10 text-ink/50" },
    };
    const s = map[status] || map.pending;
    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 ${s.cls}`}>
            <Circle size={6} weight="fill" />
            {s.label}
        </span>
    );
}

function MessageBubble({ message, isMine, myUserId, onReact, onReply, onForward, onStar, replyQuoted, highlighted, onHighlightFadeDone }) {
    const isAI = message.sender_role === "ai" || message.ai_generated;
    const [pickerOpen, setPickerOpen] = React.useState(false);
    const longPress = useLongPress(() => setPickerOpen(true));
    const isVoice = message.attachment?.kind === "voice";
    const audioSrc = isVoice
        ? `${API_BASE}/api/daddytalk/messages/${message.id}/attachment`
        : null;
    const rootRef = React.useRef(null);
    const isStarred = !!(myUserId && (message.starred_by_user_ids || []).includes(myUserId));
    const swipe = useSwipeToReply({
        onReply: () => onReply?.(message),
        onForward: () => onForward?.(message),
        isMine,
        disabled: (!onReply && !onForward) || isAI,
    });

    // V1.8.8 — flash + scroll into view when a search hit opens this message.
    React.useEffect(() => {
        if (!highlighted) return;
        rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        const t = setTimeout(() => onHighlightFadeDone?.(), 2000);
        return () => clearTimeout(t);
    }, [highlighted, onHighlightFadeDone]);

    if (isAI) {
        return (
            <div className="flex items-start gap-2" data-testid={`daddytalk-msg-ai-${message.id}`}>
                <img
                    src="/mascot/daddytalk/avatar_thinking.png"
                    alt="Uncle D"
                    className="w-8 h-8 rounded-full border-2 border-white shrink-0"
                />
                <div className="max-w-[85%] sm:max-w-[70%] px-4 py-2.5 text-sm leading-relaxed bg-gradient-to-br from-cobalt/10 to-cobalt/5 border border-cobalt/30 text-ink rounded-t-2xl rounded-br-2xl">
                    <p className="text-[10px] font-black uppercase tracking-wider text-cobalt mb-1">
                        Uncle D · AI concierge
                    </p>
                    <p className="whitespace-pre-wrap">{message.body}</p>
                    <p className="text-[9px] mt-1 text-ink/40">
                        {formatWhen(message.created_at)}
                    </p>
                </div>
            </div>
        );
    }
    return (
        <div ref={rootRef} className={`flex ${isMine ? "justify-end" : "justify-start"} relative ${highlighted ? "animate-pulse" : ""}`}>
            <SwipeReplyIndicator progress={swipe.progress} isMine={isMine} side={swipe.swipeSide} />
            <div
                ref={swipe.wrapRef}
                className={`group flex flex-col ${isMine ? "items-end" : "items-start"} max-w-[85%] sm:max-w-[70%] relative`}
                style={{
                    touchAction: "pan-y",
                }}
                {...swipe.handlers}
            >
                <QuickReactionsHoverStrip
                    onPick={(e) => onReact?.(message.id, e)}
                    onStar={onStar ? () => onStar(message.id) : undefined}
                    isStarred={isStarred}
                    isMine={isMine}
                    testIdPrefix={`daddytalk-quick-react-${message.id}`}
                />
                <div
                    {...longPress}
                    onDoubleClick={() => setPickerOpen(true)}
                    data-testid={`daddytalk-bubble-${message.id}`}
                    className={`relative px-2.5 py-1.5 text-[14px] leading-[19px] select-none transition-shadow min-w-0 max-w-full overflow-hidden ${
                        isMine
                            ? "bg-cobalt text-white rounded-2xl rounded-br-md"
                            : "bg-white border border-ink/10 text-ink rounded-2xl rounded-bl-md"
                    } ${highlighted ? "ring-4 ring-yellow-300 shadow-lg" : ""}`}
                    style={{ minWidth: "72px" }}
                >
                    {message.reply_to_message_id && replyQuoted && (
                        <ReplyQuote quoted={replyQuoted} isMine={isMine} />
                    )}
                    {isVoice ? (
                        <VoicePlayer
                            src={audioSrc}
                            durationMs={message.attachment.duration_ms}
                            transcript={message.attachment.transcript}
                            isMine={isMine}
                            testId={`daddytalk-voice-${message.id}`}
                        />
                    ) : (
                        <span className="whitespace-pre-wrap break-words">{message.body}</span>
                    )}
                    <span
                        className={`float-right ml-2 mt-1 text-[10px] leading-none whitespace-nowrap inline-flex items-center gap-1 pt-0.5 pl-1 select-none ${
                            isMine ? "text-white/75" : "text-ink/45"
                        }`}
                        data-testid={`daddytalk-bubble-meta-${message.id}`}
                    >
                        {isStarred && <span className="text-amber-400" data-testid={`daddytalk-star-${message.id}`}>★</span>}
                        {formatWhen(message.created_at)}
                    </span>
                    <span className="clear-both block" />
                    <ReactionPicker
                        open={pickerOpen}
                        onDismiss={() => setPickerOpen(false)}
                        onPick={(e) => { setPickerOpen(false); onReact?.(message.id, e); }}
                        onStar={onStar ? () => { setPickerOpen(false); onStar(message.id); } : undefined}
                        isStarred={isStarred}
                        anchor="top"
                    />
                </div>
                <ReactionBar
                    reactions={message.reactions}
                    myUserId={myUserId}
                    onToggle={(e) => onReact?.(message.id, e)}
                    testIdPrefix={`daddytalk-reaction-${message.id}`}
                />
            </div>
        </div>
    );
}

/**
 * AskTeamButton — V1.8.1 cross-context handoff.
 *
 * Dropdown with two actions from a DaddyTalk convo:
 *   1. Ask a teammate      → deep-link to /messages?tab=team&compose=dm  + candidate ctx
 *   2. Start a group huddle → deep-link to /messages?tab=team&compose=group + candidate ctx
 *
 * The URL params are read by TeamTalkInbox which auto-opens the correct
 * compose modal with `initialContext={type:"candidate", id, label}`.
 */
function AskTeamButton({ candidateName, candidateId }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    // V1.8.1 fix — never fall back to a non-candidate id. If we can't
    // resolve a real candidate user id, disable the button rather than
    // silently link the wrong entity in the created team thread.
    const disabled = !candidateId;

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    function handoff(mode) {
        if (disabled) return;
        const params = new URLSearchParams({
            tab: "team",
            compose: mode,
            candidate_id: candidateId,
            candidate_name: candidateName || "",
        });
        setOpen(false);
        navigate(`/messages?${params.toString()}`);
    }

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => !disabled && setOpen((x) => !x)}
                disabled={disabled}
                data-testid="daddytalk-ask-team-btn"
                data-open={open ? "1" : "0"}
                data-disabled={disabled ? "1" : "0"}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-emerald-600 text-xs font-semibold rounded-full ring-1 ring-emerald-200 hover:bg-emerald-500 hover:text-white hover:ring-emerald-500 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-emerald-600 disabled:hover:ring-emerald-200 transition"
                title={disabled ? "Candidate identity unknown" : "Ask a teammate about this candidate"}
            >
                <UsersThree size={11} weight="bold" />
                Ask team
                <CaretDown size={9} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div
                    role="menu"
                    data-testid="daddytalk-ask-team-menu"
                    className="absolute left-0 sm:left-auto sm:right-0 top-full mt-2 bg-white rounded-xl ring-1 ring-ink/10 shadow-xl min-w-[240px] max-w-[calc(100vw-2rem)] z-40 overflow-hidden"
                >
                    <button
                        type="button"
                        role="menuitem"
                        onClick={() => handoff("dm")}
                        data-testid="daddytalk-ask-team-dm"
                        className="w-full text-left px-4 py-3 hover:bg-cobalt hover:text-white border-b-2 border-ink/10 group"
                    >
                        <p className="text-[11px] font-black uppercase tracking-[0.1em]">Message a teammate</p>
                        <p className="text-[10px] text-ink/60 mt-0.5 group-hover:text-white/80">Quick DM about {candidateName}</p>
                    </button>
                    <button
                        type="button"
                        role="menuitem"
                        onClick={() => handoff("group")}
                        data-testid="daddytalk-ask-team-group"
                        className="w-full text-left px-4 py-3 hover:bg-cobalt hover:text-white group"
                    >
                        <p className="text-[11px] font-black uppercase tracking-[0.1em]">Start a group huddle</p>
                        <p className="text-[10px] text-ink/60 mt-0.5 group-hover:text-white/80">Loop in multiple teammates</p>
                    </button>
                </div>
            )}
        </div>
    );
}

// V1.8.8 — Search-hit list. Shown in place of the thread list when the
// search input has ≥ 2 chars.
function SearchHitList({ hits, loading, query, onPick }) {
    if (loading) {
        return (
            <div className="p-6 text-center text-xs text-ink/50" data-testid="daddytalk-search-loading">
                Searching…
            </div>
        );
    }
    if (!hits || hits.length === 0) {
        return (
            <div className="p-6 text-center" data-testid="daddytalk-search-empty">
                <MagnifyingGlass size={28} weight="light" className="mx-auto mb-2 text-ink/30" />
                <p className="font-heading font-bold text-sm mb-1">No matches</p>
                <p className="text-xs text-ink/50">
                    No messages or voice-note transcripts match “{query}”.
                </p>
            </div>
        );
    }
    return (
        <div data-testid="daddytalk-search-results">
            <p className="px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-ink/50 border-b border-ink/5 bg-cream/60">
                {hits.length} match{hits.length === 1 ? "" : "es"} for “{query}”
            </p>
            {hits.map((h) => {
                const isVoice = h.matched_field === "voice" || h.attachment_kind === "voice";
                return (
                    <button
                        key={h.message_id}
                        type="button"
                        onClick={() => onPick(h)}
                        data-testid={`daddytalk-search-hit-${h.message_id}`}
                        className="w-full text-left px-4 py-3 border-b border-ink/5 hover:bg-white transition-colors flex gap-3 items-start"
                    >
                        {h.counterparty_avatar_url ? (
                            <img
                                src={h.counterparty_avatar_url}
                                alt=""
                                className="w-9 h-9 rounded-full object-cover shrink-0 ring-1 ring-ink/10"
                            />
                        ) : (
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cobalt to-cobalt/70 text-white flex items-center justify-center text-xs font-bold shrink-0">
                                {(h.counterparty_name || "?").trim().charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 mb-0.5">
                                <p className="text-sm font-bold text-ink truncate flex-1">
                                    {h.counterparty_name}
                                </p>
                                <span
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${
                                        isVoice
                                            ? "bg-purple-100 text-purple-700"
                                            : "bg-cobalt/10 text-cobalt"
                                    }`}
                                >
                                    {isVoice ? <Microphone size={9} weight="fill" /> : <ChatText size={9} weight="fill" />}
                                    {isVoice ? "Voice" : "Text"}
                                </span>
                            </div>
                            <p className="text-xs text-ink/70 leading-snug line-clamp-2">
                                <HighlightedSnippet text={h.snippet} query={query} />
                            </p>
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

// Renders a snippet with case-insensitive highlight of the query term.
function HighlightedSnippet({ text, query }) {
    if (!query) return <>{text}</>;
    const q = query.trim();
    if (!q) return <>{text}</>;
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = String(text || "").split(new RegExp(`(${escaped})`, "gi"));
    return (
        <>
            {parts.map((p, i) =>
                p.toLowerCase() === q.toLowerCase()
                    ? <mark key={i} className="bg-yellow-200 text-ink font-semibold rounded px-0.5">{p}</mark>
                    : <React.Fragment key={i}>{p}</React.Fragment>
            )}
        </>
    );
}


